"use client"

import * as React from "react"
import { CookieIcon, SlidersHorizontalIcon } from "lucide-react"

import { Button } from "@workspace/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card"
import { Label } from "@workspace/ui/components/label"
import { Switch } from "@workspace/ui/components/switch"
import { cn } from "@workspace/ui/lib/utils"

/**
 * Cookie-consent card: Accept all / Reject all, plus a "Customise" panel with
 * one switch per optional purpose (analytics, ads) and an always-on line for
 * strictly necessary cookies.
 *
 * Purely presentational and fully controlled from outside: the caller owns
 * `open`, persists the choice and talks to the tag. Accept and Reject are the
 * SAME button tier on purpose (UK/EU guidance: refusing must be as easy as
 * accepting) and the switches start from `initialChoice` — off for a first-
 * time visitor, never pre-ticked. Non-modal (`role="dialog"` without
 * `aria-modal`): the diner can keep browsing with it open.
 */

export interface ConsentBannerChoice {
  analytics: boolean
  ads: boolean
}

export interface ConsentBannerLabels {
  title: string
  description: string
  acceptAll: string
  rejectAll: string
  customise: string
  save: string
  necessaryLabel: string
  necessaryDescription: string
  analyticsLabel: string
  analyticsDescription: string
  adsLabel: string
  adsDescription: string
}

export interface ConsentBannerProps {
  open: boolean
  labels: ConsentBannerLabels
  /** Prefills the switches, e.g. the saved choice when reopened from the footer. */
  initialChoice?: ConsentBannerChoice
  /** Start with the Customise panel expanded (the footer "Cookie settings" path). */
  defaultCustomising?: boolean
  onAcceptAll: () => void
  onRejectAll: () => void
  onSave: (choice: ConsentBannerChoice) => void
  /** `floating` pins it to the bottom of the viewport; `inline` is for galleries. */
  placement?: "floating" | "inline"
  className?: string
}

export function ConsentBanner({
  open,
  labels,
  initialChoice = { analytics: false, ads: false },
  defaultCustomising = false,
  onAcceptAll,
  onRejectAll,
  onSave,
  placement = "floating",
  className,
}: ConsentBannerProps) {
  const [customising, setCustomising] = React.useState(defaultCustomising)
  const [analytics, setAnalytics] = React.useState(initialChoice.analytics)
  const [ads, setAds] = React.useState(initialChoice.ads)
  const titleId = React.useId()
  const descriptionId = React.useId()

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className={cn(
        placement === "floating" &&
          "fixed inset-x-0 bottom-0 z-50 p-4 sm:end-auto sm:w-full sm:max-w-md",
        className
      )}
    >
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle id={titleId} className="flex items-center gap-2">
            <CookieIcon className="size-4 text-primary" aria-hidden />
            {labels.title}
          </CardTitle>
          <CardDescription id={descriptionId}>
            {labels.description}
          </CardDescription>
        </CardHeader>

        {customising ? (
          <CardContent className="flex flex-col gap-4">
            <ConsentRow
              label={labels.necessaryLabel}
              description={labels.necessaryDescription}
              checked
              disabled
            />
            <ConsentRow
              label={labels.analyticsLabel}
              description={labels.analyticsDescription}
              checked={analytics}
              onCheckedChange={setAnalytics}
            />
            <ConsentRow
              label={labels.adsLabel}
              description={labels.adsDescription}
              checked={ads}
              onCheckedChange={setAds}
            />
          </CardContent>
        ) : null}

        <CardFooter className="flex flex-wrap gap-2">
          <Button className="flex-1" onClick={onRejectAll}>
            {labels.rejectAll}
          </Button>
          <Button className="flex-1" onClick={onAcceptAll}>
            {labels.acceptAll}
          </Button>
          {customising ? (
            <Button
              variant="outline"
              className="basis-full"
              onClick={() => onSave({ analytics, ads })}
            >
              {labels.save}
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="basis-full"
              onClick={() => setCustomising(true)}
            >
              <SlidersHorizontalIcon aria-hidden />
              {labels.customise}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}

function ConsentRow({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onCheckedChange?: (checked: boolean) => void
}) {
  const id = React.useId()
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}
